import type { ActionKind } from "@puszczyk/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, unwrap } from "../../lib/api";
import { canEdit, useMe } from "../../lib/me";
import { idempotencyHeaders, useApiMutation } from "../../lib/mutation";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/dialog";
import { Field } from "../../ui/Field";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";

export function CreateActionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgs = me.memberships.filter((m) => canEdit(m.role));
  const [organizationId, setOrganizationId] = useState(orgs[0]?.organizationId ?? "");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ActionKind>("real");
  const [hqPhone, setHqPhone] = useState("");

  const create = useApiMutation(
    (_: undefined, key: string) =>
      unwrap(
        api.actions.$post(
          { json: { organizationId, name, kind, hqPhone: hqPhone.trim() || null } },
          idempotencyHeaders(key),
        ),
      ),
    {
      onSuccess: async (action) => {
        await queryClient.invalidateQueries({ queryKey: ["actions"] });
        onClose();
        await navigate({ to: "/actions/$actionId", params: { actionId: action.id } });
      },
    },
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.run();
  };

  return (
    <Dialog open={open} onClose={onClose} title={t("actions.create")}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {orgs.length > 1 && (
          <Field label={t("actions.organization")}>
            <Select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
              {orgs.map((o) => (
                <option key={o.organizationId} value={o.organizationId}>
                  {o.organizationName}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t("actions.name")}>
          <Input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t("actions.kind")}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as ActionKind)}>
            <option value="real">{t("action.real")}</option>
            <option value="exercise">{t("action.exercise")}</option>
          </Select>
        </Field>
        <Field label={t("actions.hqPhone")} hint={t("actions.hqPhoneHint")}>
          <Input type="tel" value={hqPhone} onChange={(e) => setHqPhone(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {t("common.create")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
